import axios, { type AxiosResponse } from 'axios';
import apiClient, { getErrorMessage } from './client';

export const PUBLIC_CLIENT_STATUSES = [
  'UNDER_REVIEW',
  'ACTION_REQUIRED',
  'OPENING_ACCOUNTS',
  'ACTIVE',
  'DECLINED',
] as const;

export type PublicClientStatus = (typeof PUBLIC_CLIENT_STATUSES)[number];

export type ReverificationState = 'NOT_REQUIRED' | 'QUEUED' | 'IN_PROGRESS' | 'INFORMATION_REQUESTED' | 'COMPLETED' | string;

export interface AdminPublicClientStatusDto {
  clientId: string;
  clientPublicId?: string | null;
  clientName?: string | null;
  status: PublicClientStatus;
  reasonCode?: string | null;
  reason?: string | null;
  version: number;
  publishedAt?: string | null;
  publishedByUserId?: string | null;
  reverificationState?: ReverificationState | null;
  reverificationId?: string | null;
  reverificationDueAt?: string | null;
  reverificationStartedAt?: string | null;
}

export interface ReverificationQueueItemDto extends AdminPublicClientStatusDto {
  reverificationId?: string | null;
}

export interface ReverificationDto {
  id: string;
  clientId: string;
  status: ReverificationState;
  reasonCode?: string | null;
  reasonComment?: string | null;
}

export interface ClientStatusSearchResult {
  id: string;
  publicId?: string | null;
  email?: string | null;
  companyName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface PublicClientStatusAuditEntryDto {
  id: string;
  action: string;
  fromStatus?: string | null;
  toStatus?: string | null;
  reasonCode?: string | null;
  reason?: string | null;
  version?: number | null;
  createdAt: string;
  performedByUserId?: string | null;
}

export interface PublishPublicClientStatusRequest {
  status: PublicClientStatus;
  reasonCode?: string | null;
  reason?: string | null;
  expectedVersion: number;
}

type ResultEnvelope<T> = {
  isSuccess?: boolean;
  data?: T;
  error?: string | null;
};

function isPublicStatus(value: unknown): value is PublicClientStatus {
  return typeof value === 'string' && PUBLIC_CLIENT_STATUSES.includes(value as PublicClientStatus);
}

function unwrap<T>(response: AxiosResponse<unknown>): T {
  const raw = response.data;
  if (raw !== null && typeof raw === 'object' && 'isSuccess' in raw) {
    const result = raw as ResultEnvelope<T>;
    if (result.isSuccess === false) throw new Error(result.error?.trim() || 'Request failed.');
    if (result.data === undefined) throw new Error('Empty response data.');
    return result.data;
  }
  return raw as T;
}

function normalizeStatus(raw: unknown): AdminPublicClientStatusDto {
  if (raw === null || typeof raw !== 'object') throw new Error('Unexpected public status response.');
  const value = raw as Record<string, unknown>;
  const status = value.publicStatus ?? value.status;
  if (typeof value.clientId !== 'string' || !isPublicStatus(status)) {
    throw new Error('Unexpected public status response.');
  }

  return {
    clientId: value.clientId,
    clientPublicId: typeof value.clientPublicId === 'string' ? value.clientPublicId : null,
    clientName: typeof value.clientName === 'string' ? value.clientName : typeof value.legalName === 'string' ? value.legalName : null,
    status,
    reasonCode: typeof value.reasonCode === 'string' ? value.reasonCode : null,
    reason: typeof value.reason === 'string' ? value.reason : typeof value.reasonText === 'string' ? value.reasonText : null,
    version: typeof value.version === 'number' && Number.isFinite(value.version) ? value.version : 0,
    publishedAt: typeof value.publishedAt === 'string' ? value.publishedAt : null,
    publishedByUserId: typeof value.publishedByUserId === 'string' ? value.publishedByUserId : null,
    reverificationState: typeof value.reverificationState === 'string' ? value.reverificationState : null,
    reverificationDueAt: typeof value.reverificationDueAt === 'string' ? value.reverificationDueAt : null,
    reverificationStartedAt: typeof value.reverificationStartedAt === 'string' ? value.reverificationStartedAt : null,
  };
}

function normalizeReverification(raw: unknown): ReverificationDto {
  if (raw === null || typeof raw !== 'object') throw new Error('Unexpected reverification response.');
  const value = raw as Record<string, unknown>;
  if (typeof value.id !== 'string' || typeof value.clientId !== 'string' || typeof value.status !== 'string') {
    throw new Error('Unexpected reverification response.');
  }

  return {
    id: value.id,
    clientId: value.clientId,
    status: value.status,
    reasonCode: typeof value.reasonCode === 'string' ? value.reasonCode : null,
    reasonComment: typeof value.reasonComment === 'string' ? value.reasonComment : null,
  };
}

function normalizeAudit(raw: unknown): PublicClientStatusAuditEntryDto {
  if (raw === null || typeof raw !== 'object') throw new Error('Unexpected audit response.');
  const value = raw as Record<string, unknown>;
  if (typeof value.id !== 'string' || typeof value.action !== 'string' || typeof value.createdAt !== 'string') {
    throw new Error('Unexpected audit response.');
  }

  return {
    id: value.id,
    action: value.action,
    fromStatus: typeof value.fromStatus === 'string' ? value.fromStatus : null,
    toStatus: typeof value.toStatus === 'string' ? value.toStatus : null,
    reasonCode: typeof value.reasonCode === 'string' ? value.reasonCode : null,
    reason: typeof value.comment === 'string' ? value.comment : null,
    version: typeof value.version === 'number' && Number.isFinite(value.version) ? value.version : null,
    createdAt: value.createdAt,
    performedByUserId: typeof value.performedByAdminId === 'string' ? value.performedByAdminId : null,
  };
}

export const publicClientStatusAdminApi = {
  searchClients: async (query: string): Promise<ClientStatusSearchResult[]> => {
    try {
      const response = await apiClient.get('/clients/search', { params: { q: query } });
      const data = unwrap<unknown>(response);
      const rows = Array.isArray(data) ? data : [];
      return rows.flatMap((row) => {
        if (row === null || typeof row !== 'object') return [];
        const value = row as Record<string, unknown>;
        if (typeof value.id !== 'string') return [];
        return [{
          id: value.id,
          publicId: typeof value.publicId === 'string' ? value.publicId : null,
          email: typeof value.email === 'string' ? value.email : null,
          companyName: typeof value.companyName === 'string' ? value.companyName : null,
          firstName: typeof value.firstName === 'string' ? value.firstName : null,
          lastName: typeof value.lastName === 'string' ? value.lastName : null,
        }];
      });
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },

  getReverificationQueue: async (): Promise<ReverificationQueueItemDto[]> => {
    try {
      const response = await apiClient.get('/ClientLifecycle/admin/reverification');
      const data = unwrap<unknown>(response);
      const rows = Array.isArray(data)
        ? data
        : data !== null && typeof data === 'object' && Array.isArray((data as { items?: unknown[] }).items)
          ? (data as { items: unknown[] }).items
          : [];
      return rows.map((row) => {
        const item = row as Record<string, unknown>;
        return {
          ...normalizeStatus(row),
          reverificationId: typeof item.reverificationId === 'string' ? item.reverificationId : null,
          reverificationState: typeof item.reverificationStatus === 'string' ? item.reverificationStatus : null,
          reverificationStartedAt: typeof item.createdAt === 'string' ? item.createdAt : null,
          reasonCode: typeof item.reasonCode === 'string' ? item.reasonCode : null,
          reason: typeof item.reasonComment === 'string' ? item.reasonComment : null,
        };
      });
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },

  getStatus: async (clientId: string): Promise<AdminPublicClientStatusDto | null> => {
    try {
      const response = await apiClient.get(`/ClientLifecycle/admin/clients/${encodeURIComponent(clientId)}/status`);
      return normalizeStatus(unwrap<unknown>(response));
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) return null;
      throw new Error(getErrorMessage(error));
    }
  },

  publish: async (clientId: string, request: PublishPublicClientStatusRequest): Promise<AdminPublicClientStatusDto> => {
    try {
      const changeResponse = await apiClient.put(`/ClientLifecycle/admin/clients/${encodeURIComponent(clientId)}/status`, {
        status: request.status,
        reasonCode: request.reasonCode,
        comment: request.reason,
        expectedVersion: request.expectedVersion,
      });
      const changed = normalizeStatus(unwrap<unknown>(changeResponse));
      const response = await apiClient.post(`/ClientLifecycle/admin/clients/${encodeURIComponent(clientId)}/status/publish`, {
        expectedVersion: changed.version,
      });
      return normalizeStatus(unwrap<unknown>(response));
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },

  getAudit: async (clientId: string): Promise<PublicClientStatusAuditEntryDto[]> => {
    try {
      const response = await apiClient.get(`/ClientLifecycle/admin/clients/${encodeURIComponent(clientId)}/audit`);
      const data = unwrap<unknown>(response);
      return Array.isArray(data) ? data.map((item) => normalizeAudit(item)) : [];
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },

  getReverificationHistory: async (clientId: string): Promise<ReverificationDto[]> => {
    try {
      const response = await apiClient.get(`/ClientLifecycle/admin/clients/${encodeURIComponent(clientId)}/reverification`);
      const data = unwrap<unknown>(response);
      return Array.isArray(data) ? data.map((item) => normalizeReverification(item)) : [];
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },

  startReverification: async (clientId: string, reasonCode: string, comment?: string | null): Promise<ReverificationDto> => {
    try {
      const response = await apiClient.post(`/ClientLifecycle/admin/clients/${encodeURIComponent(clientId)}/reverification`, {
        reasonCode,
        comment: comment || null,
      });
      return normalizeReverification(unwrap<unknown>(response));
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },

  requestReverificationInformation: async (id: string, documentType: string, comment?: string | null): Promise<ReverificationDto> => {
    try {
      const response = await apiClient.post(`/ClientLifecycle/admin/reverification/${encodeURIComponent(id)}/documents`, {
        documents: [{ documentType, isRequired: true }],
        comment: comment || null,
      });
      return normalizeReverification(unwrap<unknown>(response));
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },

  completeReverification: async (id: string, comment?: string | null): Promise<ReverificationDto> => {
    try {
      const response = await apiClient.post(`/ClientLifecycle/admin/reverification/${encodeURIComponent(id)}/resolve`, {
        comment: comment || null,
      });
      return normalizeReverification(unwrap<unknown>(response));
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },
};
